import * as React from "react"
import { Link, useLocation, useNavigate } from "react-router"

import { Button } from "@/components/ui/button"
import { RouteError } from "@/components/shell"
import { useCurrentUser } from "@/features/auth"
import { useReviewQuery, type ReviewId } from "@/features/review"
import { reviewLandingPath } from "@/lib/roles"

import { SignedInShell } from "./shell-route"

const TITLE = "Page not found"
const QUEUE_PATH = "/reviews"

/**
 * The review a dead address was reaching for, if it names one: `/reviews/42/output/x`, or the
 * singular slip `/review/42`. Anything else names no review.
 */
export function reviewIdIn(pathname: string): string | null {
  const match = /^\/reviews?\/(\d+)(?:\/|$)/.exec(pathname)
  return match?.[1] ?? null
}

/**
 * Any path no route claims. Matched outside the shell's guarded routes, so it is decided
 * before any sign-in check: a dead link is a dead link to everyone, and nobody is asked to
 * sign in only to be told so afterwards.
 *
 * The way out depends on who is looking and what the address was reaching for, and the
 * button always says where it goes:
 *   signed in, address names a review that exists  → back to that review
 *   signed in, otherwise                           → the review queue
 *   signed out                                     → sign in, then the review it named, if any
 */
export function NotFoundRoute(): JSX.Element {
  const user = useCurrentUser()
  const { pathname } = useLocation()
  const reviewId = reviewIdIn(pathname)

  if (user) {
    return (
      <SignedInShell title={TITLE} unknownRoute>
        <SignedInNotFound reviewId={reviewId} role={user.role} />
      </SignedInShell>
    )
  }

  return <SignedOutNotFound reviewId={reviewId} />
}

function SignedInNotFound({
  reviewId,
  role,
}: {
  reviewId: string | null
  role: Parameters<typeof reviewLandingPath>[0]
}): JSX.Element {
  const navigate = useNavigate()
  // Only asked when the address names a review. The answer is the review or nothing: a
  // missing id, one outside a client's engagement (403) and a malformed one all mean the
  // same here — there is no review to go back to, so the queue is the way out.
  // Polling is disabled: this page only needs to know whether the review exists once,
  // not watch a running review's findings arrive stage by stage.
  const review = useReviewQuery(reviewId as ReviewId | null, { poll: false })
  const exists = reviewId !== null && review.isSuccess && review.data !== null
  const checking = reviewId !== null && review.isPending

  const target = exists ? reviewLandingPath(role, reviewId) : QUEUE_PATH
  return (
    <RouteError
      kind={404}
      backLabel={
        checking
          ? "Checking the review…"
          : exists
            ? "Back to the review"
            : "Back to the review queue"
      }
      backPending={checking}
      onBack={() => {
        void navigate(target)
      }}
    />
  )
}

function SignedOutNotFound({ reviewId }: { reviewId: string | null }): JSX.Element {
  React.useEffect(() => {
    document.title = `${TITLE} · Vela`
  }, [])

  // Signed out, the review cannot be checked, so hand its address to sign-in and let the
  // review page answer once there is a session. An address that names no review signs in to
  // the plain landing page: sending them back here would only show this page again.
  const signIn = reviewId
    ? `/login?redirect=${encodeURIComponent(`/reviews/${encodeURIComponent(reviewId)}`)}`
    : "/login"

  return (
    <main className="flex min-h-dvh flex-col items-center justify-center bg-bg px-6 py-16 text-center">
      <div className="flex items-center gap-2.5">
        <img src="/vela-mark.svg" alt="" aria-hidden="true" className="size-7" />
        <span className="font-id text-[20px] font-medium leading-none tracking-[0.32em] text-brand">
          VELA
        </span>
      </div>

      <span className="mt-12 font-id text-[64px] leading-none tracking-[-0.03em] text-border-strong">
        404
      </span>
      <h1 className="mt-4 text-title text-text">{TITLE}</h1>
      <p className="mt-2.5 max-w-[46ch] text-body leading-normal text-text-2">
        The address may be mistyped or out of date. Sign in to get to your reviews.
      </p>

      <Button asChild variant="primary" className="mt-6">
        <Link to={signIn}>Sign in</Link>
      </Button>
    </main>
  )
}
